import { useQuery } from '@tanstack/react-query';

import {
  getTeamSettings,
  createTeamSettings,
  updateTeamSettings,
  deleteTeamSettings,
} from '@/features/team-settings/services/teamSettings.api';
import { isNotFoundError } from '@/shared/api/errors/apiErrors';
import { queryKeys } from '@/shared/api/queryKeys';
import { useAppMutation } from '@/shared/api/queryUtils';

import type {
  CreateTeamSettingsRequest,
  UpdateTeamSettingsRequest,
} from '@/shared/domain/teamTypes';

export function useTeamSettings(teamId: string) {
  return useQuery({
    queryKey: queryKeys.teams.settings(teamId),
    queryFn: () => getTeamSettings(teamId),
    enabled: !!teamId,
    retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 1,
  });
}

export function useCreateTeamSettings() {
  return useAppMutation(
    ({ teamId, settingsData }: { teamId: string; settingsData: CreateTeamSettingsRequest }) =>
      createTeamSettings(teamId, settingsData),
    {
      invalidates: ({ teamId }) => [queryKeys.teams.settings(teamId)],
      errorMessage: 'Failed to create team settings',
    }
  );
}

export function useUpdateTeamSettings() {
  return useAppMutation(
    ({ teamId, settingsData }: { teamId: string; settingsData: UpdateTeamSettingsRequest }) =>
      updateTeamSettings(teamId, settingsData),
    {
      invalidates: ({ teamId }) => [queryKeys.teams.settings(teamId)],
      errorMessage: 'Failed to update team settings',
    }
  );
}

export function useDeleteTeamSettings() {
  return useAppMutation(deleteTeamSettings, {
    invalidates: (teamId) => [queryKeys.teams.settings(teamId)],
    errorMessage: 'Failed to delete team settings',
  });
}
